import React, { useState, useEffect } from "react"
import { Modal, Button } from "./ui"
import { Volunteer, VolunteerUploadDocumentFormMetaProps } from "../data/types"
import { UPLOAD_DOCUMENT_TYPES } from "../data"
import { UploadIcon, XIcon } from "../icons" // Assuming you have an XIcon for clearing

type Props = {
    meta: VolunteerUploadDocumentFormMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (data: { documentType: string, file: File }) => Promise<void> | void
    volunteer: Volunteer
}

export const VolunteerUploadDocumentModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, volunteer } = props;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null)
    const [documentType, setDocumentType] = useState<string>('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    // Reset state when modal closes or opens
    useEffect(() => {
        if (!isOpen) {
            setDocumentType('');
            setSelectedFile(null);
            setLoading(false);
            setError(null);
        }
    }, [isOpen]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile || !documentType) return;

        setLoading(true);
        try {
            await onSubmit({ documentType, file: selectedFile });
            onClose();
        } catch (error) {
            console.error("Upload failed", error);
            setError(error instanceof Error ? error.message : "Failed to upload document.")
        } finally {
            setLoading(false);
        }
    };

    const footer = (
        <div className="flex justify-end gap-3">
            <Button
                type="button"
                onClick={onClose}
                variant="outline"
                color="neutral"
                disabled={loading}
            >
                {meta.cancelButton}
            </Button>
            <Button
                type="submit"
                onClick={handleUpload}
                color="primary"
                // Disable if loading OR if file/type is missing
                disabled={loading || !selectedFile || !documentType}
            >
                {loading ? meta.submittingButton : (meta.submitButton || "Upload")}
            </Button>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.title}
            footer={footer}
            maxWidth="2xl"
        >
            <div className="space-y-6 p-4">
                {error && (
                    <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded-lg">
                        <p>{error}</p>
                    </div>
                )}
                <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {meta.documentTypeLabel}
                    </label>
                    <select
                        value={documentType}
                        onChange={(e) => {
                            setDocumentType(e.target.value);
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                        <option value="" disabled>Select Document Type</option>
                        {UPLOAD_DOCUMENT_TYPES.map(dt => <option key={dt} value={dt}>{dt}</option>)}
                    </select>
                </div>

                <div>
                    <label className={`block text-xs font-medium mb-1 ${!documentType ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                        Upload File
                    </label>

                    <div className="flex items-center gap-3">
                        <input
                            type="file"
                            id="file-upload"
                            accept=".pdf,.html,.htm,text/html,.png,.jpeg,.jpg"
                            onChange={handleFileChange}
                            className="hidden"
                            disabled={!documentType}
                        />

                        <label
                            htmlFor="file-upload"
                            className={`flex-1 px-3 py-2 text-sm border rounded-lg flex items-center justify-between transition-colors
                                ${!documentType
                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                    : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600'
                                }`
                            }
                        >
                            <span className="truncate">
                                {selectedFile ? selectedFile.name : "Select a File"}
                            </span>
                            <UploadIcon className={`w-4 h-4 ms-2 ${!documentType ? 'text-gray-300' : 'text-gray-500'}`} />
                        </label>

                        {selectedFile && (
                            <Button
                                type="button"
                                variant="icon"
                                color="danger"
                                size="sm"
                                onClick={() => setSelectedFile(null)}
                                title="Remove file"
                            >
                                <XIcon className="w-4 h-4" />
                            </Button>
                        )}
                    </div>

                </div>
            </div>
        </Modal>
    );
}
